import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PRODUCT_GROUPS } from "@/data/content";
import { Reveal, SectionHead } from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const Card = ({ item, i, big }) => {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <Reveal delay={i * 0.08} className={big ? "lg:col-span-2 lg:row-span-2" : ""}>
      <motion.article whileHover="hover" onMouseMove={onMove} className={`spotlight group relative bg-white border border-line h-full ${big ? "min-h-[420px] lg:min-h-full" : "min-h-[300px]"} flex flex-col cursor-pointer`}
        onClick={() => scrollToId("estimator")} data-testid={`product-card-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <div className={`relative overflow-hidden ${big ? "flex-1" : "h-44"}`}>
          <motion.img variants={{ hover: { scale: 1.06 } }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} src={item.img} alt={item.name} className="absolute inset-0 w-full h-full object-cover" />
          {item.tag && <span className="absolute top-4 left-4 bg-ink text-linen text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5">{item.tag}</span>}
        </div>
        <div className="p-6 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-2xl text-ink">{item.name}</h3>
            <p className="mt-2 text-sm text-stone leading-relaxed">{item.desc}</p>
          </div>
          <span className="shrink-0 h-10 w-10 border border-line flex items-center justify-center text-ink group-hover:bg-brass group-hover:border-brass transition-colors duration-300"><ArrowUpRight size={16} /></span>
        </div>
      </motion.article>
    </Reveal>
  );
};

export const Products = () => {
  const [active, setActive] = useState(0);
  const group = PRODUCT_GROUPS[active];
  return (
    <section id="products" className="py-24 lg:py-36 bg-linen" data-testid="products-section">
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <SectionHead eyebrow="Products & Styles" title="Every window deserves a tailored answer." desc="From wave-fold sheers to motorized smart tracks — each treatment is cut, sewn and fitted for your exact opening." />
          <Reveal delay={0.2} className="flex gap-1 border border-line p-1 bg-white self-start lg:self-auto" data-testid="product-tabs">
            {PRODUCT_GROUPS.map((g, i) => (
              <button key={g.title} onClick={() => setActive(i)} data-testid={`product-tab-${i}`}
                className={`px-4 sm:px-5 h-11 text-[13px] font-medium tracking-wide transition-colors duration-300 ${active === i ? "bg-ink text-linen" : "text-stone hover:text-ink"}`}>
                {g.title}
              </button>
            ))}
          </Reveal>
        </div>
        <motion.div key={active} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 grid gap-5 lg:grid-cols-4 lg:grid-rows-2 lg:auto-rows-fr">
          {group.items.map((item, i) => <Card key={item.name} item={item} i={i} big={i === 0} />)}
          <Reveal delay={0.3} className="lg:col-span-2">
            <div className="h-full min-h-[200px] bg-ink text-linen p-8 flex flex-col justify-between grain">
              <p className="eyebrow">Not sure what suits?</p>
              <div>
                <p className="font-serif text-2xl lg:text-3xl mt-6">Bring the showroom to your lounge. Swatches, hardware samples and honest advice.</p>
                <button onClick={() => scrollToId("book")} className="btn-brass mt-8" data-testid="products-book-btn">Book a free measure</button>
              </div>
            </div>
          </Reveal>
        </motion.div>
      </div>
    </section>
  );
};
